import { useRef, useEffect, useMemo } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

export default function RichTextEditor({
  value,
  onChange,
  placeholder = "Enter content...",
  height = 150,
}) {
  const quillRef = useRef(null);
  const containerRef = useRef(null);
  const lastSyncedValueRef = useRef(value || "");

  // 1. Store the callback in a ref to avoid effect re-runs
  const onUpdateRef = useRef(onChange);
  useEffect(() => {
    onUpdateRef.current = onChange;
  }, [onChange]);

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        [{ font: [] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
      ],
    }),
    []
  );

  // Initialize Quill editor (Only once)
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const editorDiv = document.createElement("div");
    container.appendChild(editorDiv);

    const quill = new Quill(editorDiv, {
      theme: "snow",
      modules,
      placeholder,
    });

    quill.enable(true);

    quillRef.current = quill;

    // Set initial content
    if (value) {
      quill.root.innerHTML = value;
    }

    // Handle text changes
    quill.on("text-change", () => {
      const content = quill.root.innerHTML;
      const normalized = content === "<p><br></p>" || content === "<p></p>" ? "" : content;
      lastSyncedValueRef.current = normalized;

      // Use the ref instead of the prop directly
      if (onUpdateRef.current) {
        onUpdateRef.current(normalized);
      }
    });

    return () => {
      // Cleanup: remove the DOM elements and the Quill instance
      quillRef.current = null;
      container.innerHTML = "";
    };
    // Removed onChange and placeholder from deps to prevent re-init
  }, [modules, placeholder]);

  // Handle value updates from the outside (e.g. form reset)
  useEffect(() => {
    if (!quillRef.current) return;

    const currentContent = quillRef.current.root.innerHTML;
    const normalizedValue = value || "";
    const normalizedCurrent = currentContent === "<p><br></p>" ? "" : currentContent;
    const cameFromEditor = normalizedValue === lastSyncedValueRef.current;

    if (!cameFromEditor && normalizedCurrent !== normalizedValue) {
      // Keep parent-to-editor sync without clobbering active typing.
      quillRef.current.root.innerHTML = normalizedValue || "<p><br></p>";
      lastSyncedValueRef.current = normalizedValue;
    }
  }, [value]);

  // CSS Injection (Simplified for clarity)
  useEffect(() => {
    const styleId = "rich-text-editor-styles";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      .rich-text-editor-wrapper .ql-container { min-height: ${height}px; border-bottom-left-radius: 0.5rem; border-bottom-right-radius: 0.5rem; border: 1px solid #d1d5db; border-top: none; }
      .rich-text-editor-wrapper .ql-toolbar { border-top-left-radius: 0.5rem; border-top-right-radius: 0.5rem; border: 1px solid #d1d5db; background-color: white; }
      .rich-text-editor-wrapper .ql-editor { min-height: ${height}px; }
    `;
    document.head.appendChild(style);
  }, [height]);

  return <div className="rich-text-editor-wrapper" ref={containerRef} />;
}
